/**
 * @fileoverview useSideBar - Sidebar State Management Hook
 *
 * Manages the application sidebar state including expanded/collapsed states,
 * width calculations, and menu title management. Provides both pixel values
 * and Tailwind CSS classes for flexible layout integration.
 *
 * @module hooks/useSideBar
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Sidebar width in pixels when expanded
 * @constant {number}
 */
export const SIDEBAR_WIDTH_EXPANDED = 240; // px - Tailwind w-60

/**
 * Sidebar width in pixels when collapsed
 * @constant {number}
 */
export const SIDEBAR_WIDTH_COLLAPSED = 64; // px - Tailwind w-16

/**
 * Tailwind CSS class equivalents for sidebar dimensions.
 * Use these for consistent styling across components.
 * @constant
 */
export const SIDEBAR_CLASSES = {
  /** Width class when expanded (240px) */
  expanded: 'w-60',
  /** Width class when collapsed (64px) */
  collapsed: 'w-16',
  /** Left padding class when sidebar is expanded */
  paddingExpanded: 'pl-60',
  /** Left padding class when sidebar is collapsed */
  paddingCollapsed: 'pl-16',
} as const;

/**
 * Sidebar context value interface
 * @interface SidebarContextProps
 */
interface SidebarContextProps {
  /** Whether the sidebar is currently expanded */
  open: boolean;
  /** Setter for sidebar open state */
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Toggle function to switch between expanded/collapsed */
  toggleSidebar: () => void;
  /** Current menu title or false if none */
  titleMenu: string | boolean;
  /** Setter for menu title */
  setTitleMenu: React.Dispatch<React.SetStateAction<string | boolean>>;
  /** Current sidebar width in pixels */
  width: number;
  /** Current Tailwind padding class based on sidebar state */
  paddingClass: string;
}

/**
 * Context for sidebar state management
 * @internal
 */
const SidebarContext = createContext<SidebarContextProps | undefined>(undefined);

/**
 * Provider component for sidebar state.
 * Should wrap the main layout to provide sidebar state to all children.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 *
 * @example
 * // In your app root or layout
 * function AppLayout() {
 *   return (
 *     <SidebarProvider>
 *       <Sidebar />
 *       <MainContent />
 *     </SidebarProvider>
 *   );
 * }
 */
export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState<boolean>(true);
  const [titleMenu, setTitleMenu] = useState<string | boolean>('');

  const toggleSidebar = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const width = useMemo(() => (open ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED), [open]);

  const paddingClass = useMemo(
    () => (open ? SIDEBAR_CLASSES.paddingExpanded : SIDEBAR_CLASSES.paddingCollapsed),
    [open]
  );

  return (
    <SidebarContext.Provider
      value={{ open, setOpen, toggleSidebar, titleMenu, setTitleMenu, width, paddingClass }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

/**
 * Hook to access sidebar state and controls.
 *
 * Provides state management for the application sidebar including:
 * - Expanded/collapsed toggle
 * - Current width in pixels
 * - Tailwind CSS classes for layout
 * - Menu title management
 *
 * @returns {SidebarContextProps} Sidebar state and actions
 * @returns {boolean} returns.open - Whether sidebar is expanded
 * @returns {Function} returns.setOpen - Direct setter for open state
 * @returns {Function} returns.toggleSidebar - Toggle between expanded/collapsed
 * @returns {string|boolean} returns.titleMenu - Current menu title
 * @returns {Function} returns.setTitleMenu - Setter for menu title
 * @returns {number} returns.width - Current width in pixels (240 or 64)
 * @returns {string} returns.paddingClass - Tailwind class for content padding
 *
 * @throws {Error} If used outside of SidebarProvider
 *
 * @example
 * // Toggle button component
 * function SidebarToggle() {
 *   const { open, toggleSidebar } = useSideBar();
 *
 *   return (
 *     <button onClick={toggleSidebar}>
 *       {open ? 'Collapse' : 'Expand'}
 *     </button>
 *   );
 * }
 *
 * @example
 * // Main content with responsive padding
 * function MainContent() {
 *   const { paddingClass, width } = useSideBar();
 *
 *   return (
 *     <main className={`transition-all duration-300 ${paddingClass}`}>
 *       <p>Sidebar width: {width}px</p>
 *       {children}
 *     </main>
 *   );
 * }
 *
 * @example
 * // Using pixel width for calculations
 * function ChartContainer() {
 *   const { width: sidebarWidth } = useSideBar();
 *   const chartWidth = window.innerWidth - sidebarWidth - 40; // 40px margin
 *
 *   return <Chart width={chartWidth} />;
 * }
 */
export const useSideBar = (): SidebarContextProps => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSideBar deve ser usado dentro de um SidebarProvider');
  }
  return context;
};
